// src/services/habitLoopNotifications.ts
import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';

import { db } from '../lib/firebase';
import { FISHIQ_SPECIES_SEED } from '../constants/fishIQSeed';
import { NotificationType, View, type LogEntry, type UserProfile } from '../types';
import { buildHabitLoopNotificationCopy } from './notificationBuilder';

type HabitSignalKind = 'bite_window' | 'nearby_activity' | 'progress_milestone';
type HabitToggleKey = 'biteWindow' | 'nearbyActivity' | 'progress';

type HabitCandidate = {
  kind: HabitSignalKind;
  toggleKey: HabitToggleKey;
  priority: number;
  title: string;
  body: string;
  dedupeKey: string;
  entity?: Record<string, string>;
  link?: {
    view: View;
    targetId: string;
    subTargetId?: string;
  };
};

type TriggerHabitLoopInput = {
  userId: string;
  catchEntry: LogEntry;
  catchDiaryEntries?: LogEntry[];
  userProfile?: UserProfile | null;
};

type HabitNotificationSettings = {
  enabled?: boolean;
  biteWindow?: boolean;
  nearbyActivity?: boolean;
  progress?: boolean;
};

const MAX_HABIT_NOTIFICATIONS_PER_DAY = 1;
const HABIT_SOURCE = 'habitLoop';
const RECENT_WINDOW_MS = 72 * 60 * 60 * 1000;
const DEDUPE_WINDOW_MS = 24 * 60 * 60 * 1000;
const QUIET_HOURS_START = 21; // 9pm local time
const QUIET_HOURS_END = 6; // 6am local time

const normalize = (value: unknown) => String(value ?? '').trim().toLowerCase();

const titleCase = (value: string) =>
  value
    .split(/[_\s-]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

const dayKeyLocal = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const startOfLocalDayMs = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

const stableId = (parts: Array<string | number | null | undefined>) =>
  parts
    .map((p) => String(p ?? '').trim().replace(/[^a-zA-Z0-9_-]+/g, '_'))
    .filter(Boolean)
    .join('__')
    .slice(0, 180);

const isQuietHours = (date = new Date()) => {
  const hour = date.getHours();
  return hour < QUIET_HOURS_END || hour >= QUIET_HOURS_START;
};

const isHabitToggleEnabled = (settings: HabitNotificationSettings | undefined, key: HabitToggleKey) => {
  if (settings?.enabled === false) return false;
  return settings?.[key] !== false;
};

const getSpeciesSeed = (speciesName: string) => {
  const target = normalize(speciesName);
  if (!target) return null;

  return (
    (FISHIQ_SPECIES_SEED as any[]).find((item) => {
      const names = [item?.commonName, item?.scientificName, item?.slug, ...(Array.isArray(item?.aliases) ? item.aliases : [])]
        .map(normalize)
        .filter(Boolean);
      return names.includes(target) || names.some((name) => name && (target.includes(name) || name.includes(target)));
    }) ?? null
  );
};

const nextWindowLabel = (timesOfDay: string[] = []) => {
  const buckets = timesOfDay.map(normalize).filter(Boolean);
  const hour = new Date().getHours();

  const wantsDawn = buckets.includes('dawn');
  const wantsDusk = buckets.includes('dusk');
  const wantsNight = buckets.includes('night');
  const wantsDay = buckets.includes('day');

  if (wantsDawn && hour < 9) return 'morning low light';
  if (wantsDusk && hour < 18) return 'evening low light';
  if (wantsNight && hour < 21) return 'the after-dark window';
  if (wantsDay && hour < 15) return 'the daylight window';
  if (wantsDawn) return 'tomorrow morning low light';
  if (wantsDusk) return 'tomorrow evening low light';
  if (wantsNight) return 'tomorrow after dark';
  if (wantsDay) return 'tomorrow during daylight';
  return 'the next low-pressure window';
};

async function habitNotificationsSentToday(userId: string) {
  const q = query(
    collection(db, 'users', userId, 'notifications'),
    where('timestamp', '>=', startOfLocalDayMs()),
    limit(8),
  );

  const snap = await getDocs(q);
  return snap.docs.filter((d) => (d.data() as any)?.source === HABIT_SOURCE).length;
}

async function hasRecentDedupe(userId: string, dedupeKey: string) {
  const q = query(
    collection(db, 'users', userId, 'notifications'),
    where('dedupeKey', '==', dedupeKey),
    limit(8),
  );

  const snap = await getDocs(q);
  const cutoff = Date.now() - DEDUPE_WINDOW_MS;

  return snap.docs.some((d) => {
    const data = d.data() as any;
    const ts = Number(data?.timestamp || 0);
    return Number.isFinite(ts) && ts >= cutoff;
  });
}

async function buildNearbyActivityCandidate(userId: string, catchEntry: LogEntry): Promise<HabitCandidate | null> {
  const species = String(catchEntry.species || '').trim();
  if (!species) return null;

  try {
    const q = query(collection(db, 'communityPosts'), where('species', '==', species), limit(12));
    const snap = await getDocs(q);

    const locationKey = normalize(catchEntry.location);
    const recentCutoff = Date.now() - RECENT_WINDOW_MS;

    const matching = snap.docs
      .map((d) => ({ id: d.id, ...(d.data() as any) }))
      .filter((post: any) => {
        const authorId = String(post.authorId || post.userId || '');
        if (authorId === userId) return false;

        const visibility = normalize(post.visibility || (post.isShared ? 'public' : ''));
        if (visibility && visibility !== 'public') return false;

        const postTime = Number(post.timestamp || post.createdAtMs || 0);
        if (Number.isFinite(postTime) && postTime > 0 && postTime < recentCutoff) return false;

        const postLocation = normalize(post.location || post.waterName || post.locationName);
        if (!locationKey || !postLocation) return true;
        return postLocation.includes(locationKey) || locationKey.includes(postLocation);
      });

    if (matching.length < 1) return null;

    const water = String(catchEntry.location || '').trim();
    const targetPost = matching[0];
    const copy = buildHabitLoopNotificationCopy({
      kind: 'nearby_activity',
      species,
      water,
    });

    return {
      kind: 'nearby_activity',
      toggleKey: 'nearbyActivity',
      priority: 80,
      title: copy.title,
      body: copy.body,
      dedupeKey: stableId(['habit', 'nearby_activity', normalize(species), normalize(water || 'nearby')]),
      entity: { postId: String(targetPost.id), catchId: String(catchEntry.id || '') },
      link: { view: View.Community, targetId: String(targetPost.id) },
    };
  } catch (err) {
    console.warn('[HabitLoop] nearby activity check skipped:', err);
    return null;
  }
}

function buildBiteWindowCandidate(catchEntry: LogEntry): HabitCandidate | null {
  const species = String(catchEntry.species || '').trim();
  const seed = getSpeciesSeed(species);
  if (!species || !seed) return null;

  const times = Array.isArray(seed.bestTimesOfDay) ? seed.bestTimesOfDay : [];
  if (!times.length) return null;

  const baits = Array.isArray(seed.bestBaits) ? seed.bestBaits : [];
  const techniques = Array.isArray(seed.bestTechniques) ? seed.bestTechniques : [];
  const habitats = Array.isArray(seed.habitats) ? seed.habitats : [];

  const bait = String(baits[0] || catchEntry.lureOrBait || '').trim();
  const technique = String(techniques[0] || catchEntry.technique || '').trim();
  const habitat = habitats.length ? titleCase(String(habitats[0])) : 'cover';
  const windowLabel = nextWindowLabel(times.map(String));

  const copy = buildHabitLoopNotificationCopy({
    kind: 'bite_window',
    species,
    windowLabel,
    bait,
    technique,
    habitat,
  });

  return {
    kind: 'bite_window',
    toggleKey: 'biteWindow',
    priority: 100,
    title: copy.title,
    body: copy.body,
    dedupeKey: stableId(['habit', 'bite_window', normalize(species), normalize(windowLabel)]),
    entity: { catchId: String(catchEntry.id || ''), species },
    link: { view: View.LogCatch, targetId: String(catchEntry.id || species) },
  };
}

function buildProgressMilestoneCandidate(catchEntry: LogEntry, catchDiaryEntries?: LogEntry[]): HabitCandidate | null {
  const existingCount = Array.isArray(catchDiaryEntries) ? catchDiaryEntries.length : 0;
  const totalAfterSave = Math.max(existingCount, 0) + 1;

  const nextMilestone = Math.ceil(totalAfterSave / 5) * 5;
  const remaining = nextMilestone - totalAfterSave;

  if (remaining > 1) return null;

  const species = String(catchEntry.species || 'catch').trim();
  const copy = buildHabitLoopNotificationCopy({
    kind: 'progress_milestone',
    species,
    totalAfterSave,
    nextMilestone,
    remaining,
  });

  if (remaining === 0) {
    return {
      kind: 'progress_milestone',
      toggleKey: 'progress',
      priority: 60,
      title: copy.title,
      body: copy.body,
      dedupeKey: stableId(['habit', 'progress_milestone', nextMilestone]),
      entity: { catchId: String(catchEntry.id || '') },
      link: { view: View.CatchHistory, targetId: String(catchEntry.id || 'latest') },
    };
  }

  return {
    kind: 'progress_milestone',
    toggleKey: 'progress',
    priority: 50,
    title: copy.title,
    body: copy.body,
    dedupeKey: stableId(['habit', 'progress_nudge', nextMilestone]),
    entity: { catchId: String(catchEntry.id || '') },
    link: { view: View.LogCatch, targetId: String(catchEntry.id || 'next') },
  };
}

function pickWinner(candidates: HabitCandidate[]) {
  const priorityOrder: HabitSignalKind[] = ['bite_window', 'nearby_activity', 'progress_milestone'];

  return [...candidates].sort((a, b) => {
    const priorityDelta = b.priority - a.priority;
    if (priorityDelta !== 0) return priorityDelta;
    return priorityOrder.indexOf(a.kind) - priorityOrder.indexOf(b.kind);
  })[0] || null;
}

async function writeHabitNotification(userId: string, candidate: HabitCandidate) {
  const id = stableId(['habit', dayKeyLocal(), candidate.dedupeKey, userId]);
  const ref = doc(collection(db, 'users', userId, 'notifications'), id);
  const timestamp = Date.now();

  await setDoc(
    ref,
    {
      type: NotificationType.FISHING_ALERT,
      title: candidate.title,
      body: candidate.body,
      message: candidate.body,
      source: HABIT_SOURCE,
      habitSignalKind: candidate.kind,
      signalBased: true,
      dedupeKey: candidate.dedupeKey,
      entity: candidate.entity || {},
      link: candidate.link,
      read: false,
      timestamp,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

export async function triggerHabitLoopNotifications(input: TriggerHabitLoopInput): Promise<void> {
  const userId = String(input.userId || '').trim();
  const catchEntry = input.catchEntry;
  if (!userId || !catchEntry?.id) return;

  const settings = ((input.userProfile as any)?.notificationSettings || {}) as HabitNotificationSettings;
  if (settings.enabled === false) return;

  // Calm product rule: never surface habit-loop nudges late at night or before dawn.
  if (isQuietHours()) return;

  try {
    const sentToday = await habitNotificationsSentToday(userId);
    if (sentToday >= MAX_HABIT_NOTIFICATIONS_PER_DAY) return;

    const candidates: HabitCandidate[] = [];

    if (isHabitToggleEnabled(settings, 'biteWindow')) {
      const bite = buildBiteWindowCandidate(catchEntry);
      if (bite) candidates.push(bite);
    }

    if (isHabitToggleEnabled(settings, 'nearbyActivity')) {
      const nearby = await buildNearbyActivityCandidate(userId, catchEntry);
      if (nearby) candidates.push(nearby);
    }

    if (isHabitToggleEnabled(settings, 'progress')) {
      const progress = buildProgressMilestoneCandidate(catchEntry, input.catchDiaryEntries);
      if (progress) candidates.push(progress);
    }

    const winner = pickWinner(candidates);
    if (!winner) return;

    const duplicate = await hasRecentDedupe(userId, winner.dedupeKey);
    if (duplicate) return;

    await writeHabitNotification(userId, winner);
  } catch (err) {
    console.warn('[HabitLoop] notification trigger skipped:', err);
  }
}
